import React, { useContext, useEffect, useState } from 'react'
import { get } from 'lodash-es'
import { useIntl } from 'react-intl'

import Grid from '@mui/material/Grid'
import Edit from '@mui/icons-material/Edit'
import MenuIcon from '@mui/icons-material/Menu'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material'

import AccountBreadcrumb from './AccountBreadcrumb'
import AvatarIconAccount from '../../../public/images/icons/myAccount/avatar_user.svg'
import Link from '../Link'

import FieldsetCard from '../../Fieldset/FieldsetCard'
import getRoutes from '@/lib/getRoutes'
import pushRoute from '@/lib/pushRoute'
import getListRoutes from '@/src/getListRoutes'
import { logout } from '@/lib/hocs/withAuthSync'
import ProfileContext from '@/components/Context/ProfileContext'
import useUnreadMessages from '@/lib/hook/useUnreadMessages'
import useDisputeActionCount from '@/lib/hook/useDisputeActionCount'
import { useRouter } from 'next/router'
import Cms, { IdFormat } from '@/components/Publicity/cms'
import Card from '../Card'

type Props = {
  children: any
}
const LayoutAuth: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const { locale } = useIntl()
  const urlSubmit = getRoutes({ route: 'EditUser' })

  const { profile, loadingProfile, isAuthenticated } = useContext(ProfileContext)
  const theme = useTheme()
  const downSM = useMediaQuery(theme.breakpoints.down('sm'))
  // En dessous de md la sidebar s'empile au-dessus du contenu : on la remplace
  // par un drawer pour ne pas avoir a scroller pour atteindre les infos
  const downMD = useMediaQuery(theme.breakpoints.down('md'))
  const [openMenuAccount, setOpenMenuAccount] = useState<boolean>(false)

  // Ferme le drawer des qu'une navigation demarre (clic sur une entree du menu)
  useEffect(() => {
    const closeMenu = () => setOpenMenuAccount(false)
    router.events.on('routeChangeStart', closeMenu)

    return () => router.events.off('routeChangeStart', closeMenu)
  }, [router.events])

  // Badges menu : litiges en attente client + messages non lus.
  const disputeActionCount = useDisputeActionCount(isAuthenticated)
  const { unreadCount } = useUnreadMessages({ isAuthenticated })
  const badgeByRoute: Record<string, number> = {
    Dispute: disputeActionCount,
    Messages: unreadCount,
  }

  if (loadingProfile || !profile) return null

  const menuCompte = getListRoutes
    .filter(i => i?.displayMenuAccount && i.displayMenuAccount({ profile }))
    .sort((a, b) => {
      if (a.order < b.order) return -1
      if (a.order > b.order) return 1
      return 0
    })

  const handleLogout = async (e: any) => {
    e.stopPropagation()
    e.persist()
    await logout({ locale })
  }

  const tabPage = router['route'].replace('/[locale]/', '').split('[')

  const activeDisplayMenuAccount =
    !!router &&
    getListRoutes.find(i => {
      const regexNameRoute = new RegExp(
        i.href.replace('/', '').replace(/[!@#$%^&*()+=\-[\]\\';,./{}|":<>?~_]/gi, '\\$&'),
        'i'
      )
      return (
        regexNameRoute.test(tabPage[0]) &&
        i?.displayMenuAccount &&
        i.displayMenuAccount({ profile })
      )
    })

  const titleCard = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ marginRight: 2 }}>
        <AvatarIconAccount width={40} height={40} fill={theme.palette.primary.main} />
      </Box>

      {t({ id: 'web_mon_compte' })}
    </Box>
  )
  const accountMenuCard = (
    <FieldsetCard title={titleCard}>
      <Box sx={{ marginLeft: 2 }}>
        {menuCompte.map(oneMenu => {
          // @ts-ignore
          const link = <Link route={oneMenu.route}> {t({ id: oneMenu.text })}</Link>
          const { href } = getRoutes({ route: oneMenu.route })

          const regexNameRoute = new RegExp(
            href.replace(/[!@#$%^&*()+=\-[\]\\';,./{}|":<>?~_]/gi, '\\$&'),
            'i'
          )
          const badgeCount = badgeByRoute[oneMenu.route] || 0
          return (
            <div
              key={oneMenu.route}
              className="flex items-center font-bold h-[35px] px-2 rounded-sm"
              style={
                regexNameRoute.test(router['route'])
                  ? { background: theme.palette.primary.light }
                  : undefined
              }
            >
              {href === router['route'] ? t({ id: oneMenu.text }) : link}
              {badgeCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-bold leading-none text-white bg-red-500 rounded-full">
                  {badgeCount}
                </span>
              )}
            </div>
          )
        })}
      </Box>
      {!!downSM && (
        <div
          key={'logout'}
          className="flex items-center font-bold h-[35px] px-2 rounded-sm cursor-pointer"
          onClick={handleLogout}
        >
          {t({ id: 'web_logout' })}
        </div>
      )}

      <Cms idFormatBannerCms={IdFormat['300x250']} sxBox={{ marginTop: 2, marginBottom: 2 }} />

      <Card className="bg-[#F8F8F8] !border-none">
        <Typography className="font-bold mb-4">
          {t({ id: 'web_informations_personnelles' })}
        </Typography>

        <div>
          {get(profile, 'firstname')} {get(profile, 'lastname')}
        </div>
        <div>{get(profile, 'email')}</div>
        <div className="mt-8" />
        <div>{get(profile, 'billing.company')}</div>
        <div>{get(profile, 'billing.address')}</div>
        <div>
          {get(profile, 'billing.zipcode')} {get(profile, 'billing.city')}
        </div>
        {get(profile, 'siret') && (
          <>
            <div className="mt-8" />
            <div>{t({ id: 'web_numero_siret' })}</div>
            <div>{get(profile, 'siret')}</div>
          </>
        )}
        <div className="mt-8" />
        <div className="flex flex-row justify-between w-full">
          <div>
            <div>{t({ id: 'web_numero_tva' })}</div>
            {get(profile, 'intraEuropeanCommunityVAT')}
          </div>
          <div>
            <IconButton
              className="cursor-pointer p-1.5"
              aria-label="edit"
              onClick={() => pushRoute(urlSubmit)}
              sx={{ backgroundColor: theme.palette.background.paper }}
            >
              <Edit width={10} height={10} />
            </IconButton>
          </div>
        </div>
      </Card>
    </FieldsetCard>
  )

  return (
    <>
      <AccountBreadcrumb
        page={router['route'].replace('/[locale]/', '')}
        getListRoutes={getListRoutes}
        router={router}
      />

      <div className="mt-4" />

      {activeDisplayMenuAccount ? (
        <Grid container spacing={1}>
          {downMD ? (
            <Grid item xs={12}>
              <div
                className="flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-2 cursor-pointer"
                onClick={() => setOpenMenuAccount(true)}
                role="button"
                aria-haspopup="true"
                aria-expanded={openMenuAccount}
              >
                {titleCard}
                <MenuIcon sx={{ color: theme.palette.primary.main }} />
              </div>

              <Drawer open={openMenuAccount} onClose={() => setOpenMenuAccount(false)}>
                <div className="p-2" style={{ width: '85vw', maxWidth: 360 }}>
                  {accountMenuCard}
                </div>
              </Drawer>
            </Grid>
          ) : (
            <Grid item md={3} xs={12}>
              {accountMenuCard}
            </Grid>
          )}
          <Grid item md={9} xs={12}>
            {children}
          </Grid>
        </Grid>
      ) : (
        children
      )}
    </>
  )
}

export default LayoutAuth
